// --- START: Conditional Logger ---
// To enable logging, add `?debug=true` to the URL.
const NALA_DEBUG_MODE = false;
const NALA_DEBUG_START = performance.now();

const Logger = {
  _ts: () => `+${((performance.now() - NALA_DEBUG_START) / 1000).toFixed(3)}s`,
  log: (...args) => {
    if (NALA_DEBUG_MODE) {
      console.log(`[${Logger._ts()}]`, ...args);
    }
  },
  warn: (...args) => {
    if (NALA_DEBUG_MODE) {
      console.warn(`[${Logger._ts()}]`, ...args);
    }
  },
  // Errors are always logged regardless of debug mode.
  error: (...args) => {
    console.error(`[${Logger._ts()}]`, ...args);
  },
  group: (...args) => {
    if (NALA_DEBUG_MODE) {
      console.group(`[${Logger._ts()}]`, ...args);
    }
  },
  groupEnd: () => {
    if (NALA_DEBUG_MODE) {
      console.groupEnd();
    }
  },
};
// --- END: Conditional Logger ---

// Cart Drawer Enhancements - Rebuy Features Replication
// Configurable settings at the top for easy editing

/**
 * CartWatcher - Observes cart changes using PerformanceObserver
 * Based on: https://mikefallows.com/posts/observing-cart-changes-in-a-shopify-theme/
 *
 * WHY THIS IS BETTER:
 * - Uses PerformanceObserver to detect cart API calls (no fetch interception needed)
 * - Works with any script/app that modifies the cart (not just our code)
 * - Compares old vs new cart state to detect actual changes (not just requests)
 * - Emits clean events that other code can listen to
 * - No race conditions from trying to intercept fetch at the right time
 *
 * HOW IT WORKS:
 * 1. Stores current cart state in localStorage
 * 2. Uses PerformanceObserver to detect any /cart/* requests
 * 3. After detecting a request, fetches fresh cart state
 * 4. Compares old vs new to find what actually changed
 * 5. Emits 'cart_changed' event with oldCart, newCart, and changes
 */
class CartWatcher {
  constructor() {
    this.storageKey = "cart_watcher_last_cart";
    this.cartObserver = null;
    this.lastTriggeringRequest = null;
    this.requestHistory = [];
    this.MAX_HISTORY = 10;
  }

  init() {
    // Initialize with current cart state
    this.emitCartChanges().then(() => {
      this.observeCartChanges();
    });
  }

  async fetchCart() {
    const fetchFn = window._originalFetch || window.fetch;
    const response = await fetchFn(`${routes.cart_url}.js`);
    if (!response.ok) {
      Logger.error("[CartWatcher] Failed to fetch cart");
      return null;
    }
    return await response.json();
  }

  storeCart(cart) {
    if (!cart) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(cart));
    } catch (e) {
      Logger.warn("[CartWatcher] Failed to store cart:", e);
    }
  }

  getStoredCart() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : { items: [] };
    } catch (e) {
      Logger.warn("[CartWatcher] Failed to get stored cart:", e);
      return { items: [] };
    }
  }

  findCartChanges(oldCart, newCart) {
    if (!oldCart || !oldCart.items) oldCart = { items: [] };
    if (!newCart || !newCart.items) newCart = { items: [] };

    const onlyInLeft = (l, r) =>
      l.filter((li) => !r.some((ri) => li.key === ri.key));

    let result = {
      added: onlyInLeft(newCart.items, oldCart.items),
      removed: onlyInLeft(oldCart.items, newCart.items),
    };

    // Check for quantity changes in existing items
    oldCart.items.forEach((oi) => {
      const ni = newCart.items.find(
        (i) => i.key === oi.key && i.quantity !== oi.quantity,
      );
      if (!ni) return;

      let quantity = ni.quantity - oi.quantity;
      let item = { ...ni };
      item.quantity = Math.abs(quantity);

      if (quantity > 0) {
        result.added.push(item);
      } else {
        result.removed.push(item);
      }
    });

    return result;
  }

  async emitCartChanges() {
    const newCart = await this.fetchCart();
    if (!newCart) return;

    const oldCart = this.getStoredCart();
    const changes = this.findCartChanges(oldCart, newCart);

    // Only emit event if there are actual changes
    if (changes.added.length > 0 || changes.removed.length > 0) {
      Logger.log("[CartWatcher] Change detected:", {
        added: changes.added.length,
        removed: changes.removed.length,
        itemCount: newCart.item_count,
      });

      // Check if free gift was removed (for debugging)
      const freeGiftRemoved = changes.removed.find(
        (item) => item.properties?._free_gift === "true",
      );

      if (freeGiftRemoved) {
        Logger.warn("[CartWatcher] Free gift removed");
      }

      const event = new CustomEvent("cart_changed", {
        detail: {
          oldCart,
          newCart,
          changes,
          triggeringRequest: this.lastTriggeringRequest,
        },
      });
      window.dispatchEvent(event);

      this.lastTriggeringRequest = null;
    }

    this.storeCart(newCart);
    return newCart;
  }

  observeCartChanges() {
    // Check if PerformanceObserver is supported
    if (typeof PerformanceObserver === "undefined") {
      Logger.warn(
        "[CartWatcher] PerformanceObserver not supported, falling back to polling",
      );
      // Fallback: poll every 2 seconds (only if needed)
      setInterval(() => this.emitCartChanges(), 2000);
      return;
    }

    this.cartObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const isValidRequestType = ["xmlhttprequest", "fetch"].includes(
          entry.initiatorType,
        );
        const isCartChangeRequest = /\/cart\//.test(entry.name);

        if (isValidRequestType && isCartChangeRequest) {
          // Check if this looks like a bundle app request (do this first)
          const isUpdateRequest = entry.name.includes("/cart/update");
          const isAddRequest = entry.name.includes("/cart/add");
          const isChangeRequest = entry.name.includes("/cart/change");

          // Track ALL cart requests (including reads) to see the sequence
          const requestInfo = {
            url: entry.name,
            timestamp: Date.now(),
            duration: entry.duration,
            initiatorType: entry.initiatorType,
            startTime: entry.startTime,
            isModifying:
              !entry.name.includes("/cart.js") &&
              !entry.name.includes("sections="),
            requestType: isUpdateRequest
              ? "UPDATE (often bundle apps)"
              : isAddRequest
                ? "ADD"
                : isChangeRequest
                  ? "CHANGE (quantity/remove)"
                  : entry.name.includes("/cart.js")
                    ? "READ"
                    : "UNKNOWN",
          };

          // Add to history
          this.requestHistory.push(requestInfo);
          if (this.requestHistory.length > this.MAX_HISTORY) {
            this.requestHistory.shift();
          }

          // Only process modifying requests
          if (requestInfo.isModifying) {
            this.lastTriggeringRequest = requestInfo;

            Logger.log("[CartWatcher] Cart request:", requestInfo.requestType);

            // Short delay to let the request complete
            setTimeout(() => {
              this.emitCartChanges();
            }, 30);
          }
        }
      });
    });

    try {
      this.cartObserver.observe({ entryTypes: ["resource"] });
      Logger.log("[CartWatcher] Started observing cart changes");
    } catch (e) {
      Logger.error("[CartWatcher] Failed to observe:", e);
    }
  }

  destroy() {
    if (this.cartObserver) {
      this.cartObserver.disconnect();
      this.cartObserver = null;
    }
  }
}

const DEFAULT_CART_ENHANCEMENT_CONFIG = {
  // Free gift variant ID - this is what gets added to cart
  // Update this when changing the gift product
  FREE_GIFT_VARIANT_ID: "49254513672425",

  // Mystery gift product handle
  MYSTERY_GIFT_HANDLE: "mystery-gift",

  // Mystery gift eligibility - URL parameters that trigger eligibility
  MYSTERY_GIFT_URL_TRIGGERS: ["gap flow", "gap+flow"],

  // Mystery gift cookie name and duration (24 hours)
  MYSTERY_GIFT_COOKIE_NAME: "mystery_gift",
  MYSTERY_GIFT_COOKIE_DURATION_HOURS: 24,

  // Timer duration in minutes
  TIMER_DURATION_MINUTES: 10,

  // Tier thresholds in cents (100 = $1)
  TIERS: {
    AUD: {
      TIER_1: 15000, // $150 - Free Express Shipping
      TIER_2: 18000, // $180 - Free Hat
      TIER_1_LABEL: "Free Express Shipping",
      TIER_2_LABEL: "Free Cosmetic Bag",
      TIER_1_MESSAGE: (remaining) =>
        `You Are $${remaining} Away From Free Express Shipping!`,
      TIER_2_MESSAGE: (remaining) =>
        `You Are $${remaining} Away From Free Cosmetic Bag!`,
      TIER_1_COMPLETE: "You've Unlocked Free Express Shipping!",
      TIER_2_COMPLETE: "You've Unlocked Free Cosmetic Bag!",
    },
    NZD: {
      TIER_1: 15000, // $150 - Free Shipping
      TIER_2: 18000, // $180 - Free Hat
      TIER_1_LABEL: "Free Shipping",
      TIER_2_LABEL: "Free Cosmetic Bag",
      TIER_1_MESSAGE: (remaining) =>
        `You Are $${remaining} Away From Free Shipping!`,
      TIER_2_MESSAGE: (remaining) =>
        `You Are $${remaining} Away From Free Cosmetic Bag!`,
      TIER_1_COMPLETE: "You've Unlocked Free Shipping!",
      TIER_2_COMPLETE: "You've Unlocked Free Cosmetic Bag!",
    },
  },

  // Supported currencies for progress bar (only show for these)
  SUPPORTED_CURRENCIES: ["AUD", "NZD"],
};

// Initialize global config if it doesn't exist
window.CART_ENHANCEMENT_CONFIG = window.CART_ENHANCEMENT_CONFIG || {};

// Merge logic for TIERS (preserve defaults if missing in override)
const _mergedTiers = { ...DEFAULT_CART_ENHANCEMENT_CONFIG.TIERS };
if (window.CART_ENHANCEMENT_CONFIG.TIERS) {
  for (const currency in window.CART_ENHANCEMENT_CONFIG.TIERS) {
    if (_mergedTiers[currency]) {
      _mergedTiers[currency] = {
        ..._mergedTiers[currency],
        ...window.CART_ENHANCEMENT_CONFIG.TIERS[currency],
      };
    } else {
      _mergedTiers[currency] = window.CART_ENHANCEMENT_CONFIG.TIERS[currency];
    }
  }
}

// Apply everything to the global object
// We use Object.assign to update the existing global object reference
// instead of replacing it, which preserves references if other scripts captured it.
Object.assign(window.CART_ENHANCEMENT_CONFIG, {
  ...DEFAULT_CART_ENHANCEMENT_CONFIG,
  ...window.CART_ENHANCEMENT_CONFIG,
  TIERS: _mergedTiers,
});

// Local reference for internal use (same pointer as global)
const CART_ENHANCEMENT_CONFIG = window.CART_ENHANCEMENT_CONFIG;

// Debug: Log final config to verify A/B test is applied correctly
Logger.log("[Config] Final config:", {
  GIFT_TIER: CART_ENHANCEMENT_CONFIG.GIFT_TIER || "TIER_2 (default)",
  FREE_GIFT_VARIANT_ID: CART_ENHANCEMENT_CONFIG.FREE_GIFT_VARIANT_ID,
  AUD_TIER_1: CART_ENHANCEMENT_CONFIG.TIERS?.AUD?.TIER_1 / 100,
  AUD_TIER_2: CART_ENHANCEMENT_CONFIG.TIERS?.AUD?.TIER_2 / 100,
  hasIcons: !!CART_ENHANCEMENT_CONFIG.ICONS,
});

// Helper function to get color swatch URL
function getColorSwatchURL(variantName) {
  if (!variantName) return null;
  const baseurl = "https://cdn.shopify.com/s/files/1/0651/7083/3641/files/";
  let sanitizedVariantName = variantName
    .replace(/['&®]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase();
  sanitizedVariantName = sanitizedVariantName.replace(/\//g, "-");
  return `${baseurl}${sanitizedVariantName}.jpg`;
}

// Timer Management Class - Global timer that runs continuously
class CartTimer {
  constructor() {
    this.intervalId = null;
    this.STORAGE_KEY = "cart_drawer_timer_end";
    this.DURATION = CART_ENHANCEMENT_CONFIG.TIMER_DURATION_MINUTES * 60 * 1000;
    this.endTime = null;
    this.isRunning = false;
  }

  init() {
    // If already running, don't restart
    if (this.isRunning && this.intervalId) {
      return;
    }

    // Get or set end time
    const storedEndTime = this.getEndTime();
    if (storedEndTime && storedEndTime > Date.now()) {
      this.endTime = storedEndTime;
    } else {
      this.endTime = Date.now() + this.DURATION;
      localStorage.setItem(this.STORAGE_KEY, this.endTime.toString());
    }

    // Start the countdown
    this.start();
  }

  getEndTime() {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  }

  start() {
    // Clear any existing interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.isRunning = true;
    const self = this;

    // Update function
    const tick = function () {
      const minutesEl = document.getElementById("cart-timer-minutes");
      const secondsEl = document.getElementById("cart-timer-seconds");

      if (!minutesEl || !secondsEl) {
        return;
      }

      const now = Date.now();
      let remaining = Math.max(0, self.endTime - now);

      if (remaining <= 0) {
        // Timer expired - reset
        self.endTime = Date.now() + self.DURATION;
        localStorage.setItem(self.STORAGE_KEY, self.endTime.toString());
        remaining = self.DURATION;
      }

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      minutesEl.textContent = minutes.toString().padStart(2, "0");
      secondsEl.textContent = seconds.toString().padStart(2, "0");
    };

    // Run immediately
    tick();

    // Then run every second
    this.intervalId = setInterval(tick, 1000);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
  }

  destroy() {
    this.stop();
  }
}

// Progress Bar Management Class
class CartProgressBar {
  constructor() {
    // State tracking
    this.state = {
      percentage: 0,
      tier1Complete: false,
      tier2Complete: false,
      promptText: "",
      isVisible: false,
      currency: null,
    };

    // DOM element cache (populated on first use or after DOM change)
    this.elements = null;
    this.elementsVersion = 0;

    // Debounce timer for rapid updates
    this.updateDebounceTimer = null;

    // Setup MutationObserver to detect DOM changes and re-apply state
    this.setupDOMObserver();
  }

  setupDOMObserver() {
    // Observe the cart drawer for DOM changes
    const observer = new MutationObserver((mutations) => {
      // Check if progress bar elements were replaced
      const progressFill = document.getElementById("cart-progress-bar-fill");
      if (progressFill && this.state.percentage > 0) {
        // Check if the fill width doesn't match our state
        const currentWidth = parseFloat(progressFill.style.width) || 0;
        if (Math.abs(currentWidth - this.state.percentage) > 1) {
          // DOM was replaced, re-apply state immediately without transition
          this.applyStateToDOM(true);
        }
      }
    });

    // Start observing when cart drawer exists
    const startObserving = () => {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        observer.observe(cartDrawer, {
          childList: true,
          subtree: true,
        });
      } else {
        // Retry after a short delay
        setTimeout(startObserving, 100);
      }
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", startObserving);
    } else {
      startObserving();
    }
  }

  /**
   * Get or cache DOM elements. Returns null if critical elements missing.
   * Uses version tracking to detect when elements need re-querying.
   */
  getElements(forceRefresh = false) {
    // Check if we need to refresh (forced or elements missing)
    if (
      forceRefresh ||
      !this.elements ||
      !document.body.contains(this.elements.section)
    ) {
      const section = document.getElementById("cart-drawer-progress-section");

      if (!section) {
        this.elements = null;
        return null;
      }

      this.elements = {
        section,
        fill: document.getElementById("cart-progress-bar-fill"),
        prompt: document.getElementById("cart-progress-bar-prompt"),
        meter: document.getElementById("cart-progress-bar-meter"),
        step1: document.getElementById("cart-progress-step-1"),
        step2: document.getElementById("cart-progress-step-2"),
        label1: document.getElementById("cart-progress-label-1"),
        label2: document.getElementById("cart-progress-label-2"),
        price1: document.getElementById("cart-progress-price-1"),
        price2: document.getElementById("cart-progress-price-2"),
        limitedSupply: document.getElementById("cart-progress-limited-supply"),
        timerHeader: document.getElementById("cart-drawer-timer-header"),
      };

      this.elementsVersion++;

      // Apply current state to fresh elements immediately (prevents flash)
      this.applyStateToDOM(true);
    }

    return this.elements;
  }

  /**
   * Force re-query of DOM elements (call after cart drawer HTML is replaced)
   */
  reinitElements() {
    this.getElements(true);
  }

  /**
   * Apply current state to DOM elements
   * @param {boolean} immediate - If true, skip transition for instant update
   */
  applyStateToDOM(immediate = false) {
    // Re-query elements directly to ensure we have fresh references
    const fill = document.getElementById("cart-progress-bar-fill");
    const step1 = document.getElementById("cart-progress-step-1");
    const step2 = document.getElementById("cart-progress-step-2");
    const prompt = document.getElementById("cart-progress-bar-prompt");
    const meter = document.getElementById("cart-progress-bar-meter");
    const section = document.getElementById("cart-drawer-progress-section");
    const timerHeader = document.getElementById("cart-drawer-timer-header");

    // Update visibility
    if (section) {
      section.style.display = this.state.isVisible ? "block" : "none";
    }
    if (timerHeader) {
      timerHeader.style.display = this.state.isVisible ? "flex" : "none";
    }

    if (!this.state.isVisible) {
      // Remove updating class even if not visible
      if (section) section.classList.remove("is-updating");
      return;
    }

    // Update icons if overridden in config
    if (CART_ENHANCEMENT_CONFIG.ICONS) {
      if (step1 && CART_ENHANCEMENT_CONFIG.ICONS.TIER_1) {
        step1.innerHTML = CART_ENHANCEMENT_CONFIG.ICONS.TIER_1;
      }
      if (step2 && CART_ENHANCEMENT_CONFIG.ICONS.TIER_2) {
        step2.innerHTML = CART_ENHANCEMENT_CONFIG.ICONS.TIER_2;
      }
    }

    // Update labels, prices, and limited supply tag to match current tier configuration
    const label1 = document.getElementById("cart-progress-label-1");
    const label2 = document.getElementById("cart-progress-label-2");
    const price1El = document.getElementById("cart-progress-price-1");
    const price2El = document.getElementById("cart-progress-price-2");
    const limitedSupplyEl = document.getElementById(
      "cart-progress-limited-supply",
    );
    if (
      this.state.currency &&
      CART_ENHANCEMENT_CONFIG.TIERS[this.state.currency]
    ) {
      const tiers = CART_ENHANCEMENT_CONFIG.TIERS[this.state.currency];
      if (label1) label1.textContent = tiers.TIER_1_LABEL;
      if (label2) label2.textContent = tiers.TIER_2_LABEL;
      if (price1El)
        price1El.textContent = `$${(tiers.TIER_1 / 100).toFixed(0)}`;
      if (price2El)
        price2El.textContent = `$${(tiers.TIER_2 / 100).toFixed(0)}`;
      // Position limited supply tag under the gift tier
      if (limitedSupplyEl) {
        const giftTier = CART_ENHANCEMENT_CONFIG.GIFT_TIER || "TIER_2";
        const targetGroupId =
          giftTier === "TIER_1"
            ? "cart-progress-label-group-1"
            : "cart-progress-label-group-2";
        const targetGroup = document.getElementById(targetGroupId);
        if (targetGroup && limitedSupplyEl.parentElement !== targetGroup) {
          targetGroup.appendChild(limitedSupplyEl);
        }
      }
      this.updateAnnouncementBar(this.state.currency);
    }

    // Update progress bar fill
    if (fill) {
      if (immediate) {
        fill.style.transition = "none";
        fill.style.width = `${this.state.percentage}%`;
        // Force reflow then restore transition
        fill.offsetHeight;
        // Restore transition after a micro-task
        requestAnimationFrame(() => {
          fill.style.transition = "width 0.3s ease";
        });
      } else {
        fill.style.transition = "width 0.3s ease";
        fill.style.width = `${this.state.percentage}%`;
      }
    }

    // Update tier icons
    if (step1) {
      step1.classList.toggle("complete", this.state.tier1Complete);
    }
    if (step2) {
      step2.classList.toggle("complete", this.state.tier2Complete);
    }

    // Update prompt text
    if (prompt) {
      prompt.textContent = this.state.promptText;
    }

    // Update ARIA
    if (meter) {
      meter.setAttribute("aria-valuenow", Math.round(this.state.percentage));
    }
  }

  /**
   * Calculate cart total excluding free gift items
   */
  calculateCartTotal(cart) {
    if (!cart || !cart.items) return 0;

    let total = cart.total_price;

    for (const item of cart.items) {
      // Exclude any item with _free_gift property
      if (item.properties && item.properties._free_gift === "true") {
        total -= item.price * item.quantity;
      }
    }

    return Math.max(0, total);
  }

  /**
   * Calculate progress state from cart data
   * Returns new state object without modifying DOM
   */
  calculateState(cart) {
    const currency = cart?.currency || "AUD";
    const isSupported =
      CART_ENHANCEMENT_CONFIG.SUPPORTED_CURRENCIES.includes(currency);
    const hasItems = cart && cart.item_count > 0;

    // Determine visibility
    const isVisible = isSupported && hasItems;

    if (!isVisible) {
      return {
        percentage: 0,
        tier1Complete: false,
        tier2Complete: false,
        promptText: "",
        isVisible: false,
        currency,
      };
    }

    const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
    if (!tiers) {
      return {
        percentage: 0,
        tier1Complete: false,
        tier2Complete: false,
        promptText: "",
        isVisible: false,
        currency,
      };
    }

    const cartTotal = this.calculateCartTotal(cart);

    let percentage = 0;
    let promptText = "";
    let tier1Complete = false;
    let tier2Complete = false;

    if (cartTotal < tiers.TIER_1) {
      // Before tier 1: Progress 0% to 25% (icon 1 at 25%)
      const remaining = tiers.TIER_1 - cartTotal;
      percentage = (cartTotal / tiers.TIER_1) * 25;
      promptText = tiers.TIER_1_MESSAGE((remaining / 100).toFixed(2));
      tier1Complete = false;
      tier2Complete = false;
    } else if (cartTotal < tiers.TIER_2) {
      // Between tier 1 and tier 2: Progress 25% to 75% (icon 2 at 75%)
      const remaining = tiers.TIER_2 - cartTotal;
      const progressInTier2 =
        (cartTotal - tiers.TIER_1) / (tiers.TIER_2 - tiers.TIER_1);
      percentage = 25 + progressInTier2 * 50;
      promptText = tiers.TIER_2_MESSAGE((remaining / 100).toFixed(2));
      tier1Complete = true;
      tier2Complete = false;
    } else {
      // Reached tier 2: Fill to 100%
      percentage = 100;
      promptText = tiers.TIER_2_COMPLETE;
      tier1Complete = true;
      tier2Complete = true;
    }

    return {
      percentage: Math.min(100, Math.max(0, percentage)),
      tier1Complete,
      tier2Complete,
      promptText,
      isVisible: true,
      currency,
    };
  }

  /**
   * Update tier labels based on currency
   */
  updateLabels(currency) {
    const el = this.elements;
    if (!el) return;

    const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
    if (!tiers) return;

    if (el.label1) el.label1.textContent = tiers.TIER_1_LABEL;
    if (el.label2) el.label2.textContent = tiers.TIER_2_LABEL;
    if (el.price1)
      el.price1.textContent = `$${(tiers.TIER_1 / 100).toFixed(0)}`;
    if (el.price2)
      el.price2.textContent = `$${(tiers.TIER_2 / 100).toFixed(0)}`;
  }

  /**
   * Update announcement bar messages from config (so Variant A $200 vs Variant B $130 is correct)
   */
  updateAnnouncementBar(currency) {
    const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
    if (!tiers) return;

    const bar = document.getElementById("cart-drawer-announcement-bar");
    if (!bar) return;

    const messages = bar.querySelectorAll(".announcement-message");
    const t1 = (tiers.TIER_1 / 100).toFixed(0);
    const t2 = (tiers.TIER_2 / 100).toFixed(0);
    if (messages[0])
      messages[0].textContent = `${tiers.TIER_1_LABEL.toUpperCase()} OVER $${t1}`;
    if (messages[1])
      messages[1].textContent = `${tiers.TIER_2_LABEL.toUpperCase()} OVER $${t2}`;
  }

  /**
   * Update visibility of progress section and timer header
   */
  updateVisibility(isVisible) {
    const el = this.elements;
    if (!el) return;

    if (el.section) {
      el.section.style.display = isVisible ? "block" : "none";
    }
    if (el.timerHeader) {
      el.timerHeader.style.display = isVisible ? "flex" : "none";
    }
  }

  /**
   * Main update method - calculates state and updates DOM
   * @param {Object} cart - Shopify cart object
   * @param {boolean} forceRefresh - Force DOM element refresh
   */
  update(cart, forceRefresh = false) {
    // Get/refresh elements
    const el = this.getElements(forceRefresh);

    if (!el) {
      Logger.warn("CartProgressBar: Progress section not found in DOM");
      return;
    }

    // Calculate new state
    const newState = this.calculateState(cart);

    // Check if state actually changed (avoid unnecessary DOM updates)
    const stateChanged =
      this.state.percentage !== newState.percentage ||
      this.state.tier1Complete !== newState.tier1Complete ||
      this.state.tier2Complete !== newState.tier2Complete ||
      this.state.promptText !== newState.promptText ||
      this.state.isVisible !== newState.isVisible ||
      this.state.currency !== newState.currency;

    if (!stateChanged && !forceRefresh) {
      return; // No changes, skip DOM updates
    }

    // Store new state
    this.state = newState;

    // Update visibility
    this.updateVisibility(newState.isVisible);

    if (!newState.isVisible) {
      return; // Hidden, no need to update content
    }

    // Update labels if currency changed
    if (this.state.currency !== newState.currency || forceRefresh) {
      this.updateLabels(newState.currency);
    }

    // Apply state to DOM with animation
    this.applyStateToDOM(false);
  }
}

// Free Gift Manager Class - Uses static config, no product fetching
class FreeGiftManager {
  constructor() {
    this.isProcessing = false;
    this.lastOperationTime = 0; // Track when we last added/removed gift
    this.lastCheckTime = 0; // Debounce checks
    this.CHECK_DEBOUNCE = 100; // ms between checks (reduced for faster response)
  }

  /**
   * Main entry point, called by the cart_changed event.
   * Trusts the cart object it receives and acts immediately.
   */
  async checkAndManage(cart) {
    if (this.isProcessing || !cart) {
      return;
    }

    // Debounce rapid checks
    const now = Date.now();
    if (now - this.lastCheckTime < this.CHECK_DEBOUNCE) {
      return;
    }
    this.lastCheckTime = now;

    this.isProcessing = true;
    Logger.log(`[FreeGiftManager] Checking cart state...`);

    try {
      const currency = cart.currency || "AUD";
      if (!CART_ENHANCEMENT_CONFIG.SUPPORTED_CURRENCIES.includes(currency)) {
        this.isProcessing = false;
        return;
      }

      const tiers = CART_ENHANCEMENT_CONFIG.TIERS[currency];
      if (!tiers) {
        this.isProcessing = false;
        return;
      }

      const freeGiftItems = this.findAllFreeGiftsInCart(cart);
      const hasGift = freeGiftItems.length > 0;
      const cartTotal = this.calculateCartTotalExcludingGift(cart);
      const regularItemCount = this.countRegularItems(cart);

      // Determine which tier has the gift (default to TIER_2 for backward compatibility)
      const giftTier = CART_ENHANCEMENT_CONFIG.GIFT_TIER || "TIER_2";
      const giftThreshold = tiers[giftTier];
      const shouldHaveGift = cartTotal >= giftThreshold && regularItemCount > 0;

      Logger.log(
        `[FreeGiftManager] State: shouldHaveGift=${shouldHaveGift}, hasGift=${hasGift}`,
        {
          giftTier,
          giftThreshold: giftThreshold / 100,
          cartTotal: cartTotal / 100,
          regularItemCount,
          giftCount: freeGiftItems.length,
        },
      );

      // PREVENT DUPLICATE GIFTS: Remove extras if more than 1
      if (freeGiftItems.length > 1) {
        Logger.log(
          `[FreeGiftManager] Found ${freeGiftItems.length} gifts - removing duplicates`,
        );
        // Keep first, remove the rest
        for (let i = 1; i < freeGiftItems.length; i++) {
          await this._removeFreeGift(freeGiftItems[i]);
        }
        return; // Exit after cleanup, next cycle will handle correctly
      }

      // PREVENT GIFT-ONLY CART: Remove gift if no regular items
      if (regularItemCount === 0 && hasGift) {
        Logger.log(`[FreeGiftManager] Cart has only gift(s), removing...`);
        await this._removeFreeGift(freeGiftItems[0]);
        return;
      }

      if (shouldHaveGift && !hasGift) {
        Logger.log(`[FreeGiftManager] Action: Adding gift.`);
        await this._addFreeGift();
      } else if (!shouldHaveGift && hasGift) {
        Logger.log(`[FreeGiftManager] Action: Removing gift.`);
        await this._removeFreeGift(freeGiftItems[0]);
      }
    } catch (error) {
      Logger.error("[FreeGiftManager] Error:", error);
    } finally {
      this.isProcessing = false;
      Logger.log(`[FreeGiftManager] Check complete.`);
    }
  }

  /**
   * Placeholder for checkout button updates (disabled feature)
   */
  updateCheckoutButton(hasRegularItems) {
    // Checkout button is never disabled - users can always proceed
  }

  /**
   * Helper to calculate cart total excluding free gift
   * Checks _free_gift property only (not handle) so gift product can be changed
   */
  calculateCartTotalExcludingGift(cart) {
    if (!cart || !cart.items) return 0;

    let total = cart.total_price;
    for (const item of cart.items) {
      if (item.properties && item.properties._free_gift === "true") {
        total -= item.price * item.quantity;
      }
    }
    return Math.max(0, total);
  }

  /**
   * Helper to check if free gift is in cart (returns first match)
   * Checks _free_gift property only
   */
  findFreeGiftInCart(cart) {
    if (!cart || !cart.items) return null;
    return cart.items.find(
      (item) => item.properties && item.properties._free_gift === "true",
    );
  }

  /**
   * Helper to find ALL free gifts in cart (for duplicate detection)
   * Checks _free_gift property only
   */
  findAllFreeGiftsInCart(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(
      (item) => item.properties && item.properties._free_gift === "true",
    );
  }

  /**
   * Helper to count regular (non-gift) items
   */
  countRegularItems(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.filter(
      (item) => !(item.properties && item.properties._free_gift === "true"),
    ).length;
  }

  /**
   * Adds the free gift to the cart and immediately refreshes UI.
   * Uses sections API to minimize network requests.
   */
  async _addFreeGift() {
    this.lastOperationTime = Date.now();
    const fetchFn = window._originalFetch || window.fetch;
    const variantId = CART_ENHANCEMENT_CONFIG.FREE_GIFT_VARIANT_ID;

    Logger.log("[FreeGiftManager] Action: Adding gift...");

    try {
      // Add gift AND get sections in one request
      const response = await fetchFn(`${routes.cart_add_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _free_gift: "true" },
          sections: "cart-drawer,cart-icon-bubble",
          sections_url: routes.cart_url,
        }),
      });

      this.lastOperationTime = Date.now();

      if (response.ok) {
        const data = await response.json();

        // Update UI from sections if available
        if (data.sections) {
          this._updateCartDrawerFromSections(data.sections);
        } else {
          // Fallback to full refresh
          await refreshCartDrawerWithReinit();
        }

        // Progress bar doesn't need updating here - it excludes free gifts from
        // its total calculation, so the value doesn't change when a gift is added.
        // CartWatcher will update the counter on the next cycle.
        Logger.log("[FreeGiftManager] Gift added and UI refreshed");
      }
    } catch (e) {
      Logger.error("[FreeGiftManager] Add gift failed:", e);
    }
  }

  /**
   * Removes the free gift from the cart and immediately refreshes UI.
   * Uses sections API - /cart/update.js returns full cart + sections in ONE request.
   */
  async _removeFreeGift(giftItem) {
    if (!giftItem || !giftItem.key) return;

    this.lastOperationTime = Date.now();
    const fetchFn = window._originalFetch || window.fetch;

    Logger.log("[FreeGiftManager] Action: Removing gift...");

    try {
      // Remove gift AND get sections in one request
      const response = await fetchFn(`${routes.cart_update_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          updates: { [giftItem.key]: 0 },
          sections: "cart-drawer,cart-icon-bubble",
          sections_url: routes.cart_url,
        }),
      });

      this.lastOperationTime = Date.now();

      if (response.ok) {
        const cart = await response.json();

        // Update progress bar and counter with cart data from response
        if (window.cartEnhancements) {
          window.cartEnhancements.progressBar.update(cart);
          window.cartEnhancements.itemCounter.update(cart);
        }

        // Update UI from sections if available
        if (cart.sections) {
          this._updateCartDrawerFromSections(cart.sections);
        } else {
          // Fallback to full refresh if sections not in response
          await refreshCartDrawerWithReinit();
        }

        Logger.log("[FreeGiftManager] Gift removed and UI refreshed");
      }
    } catch (e) {
      Logger.error("[FreeGiftManager] Remove gift failed:", e);
    }
  }

  /**
   * Update cart drawer UI from sections response.
   * Handles the Shopify section wrapper structure.
   */
  _updateCartDrawerFromSections(sections) {
    if (!sections) return;

    const cartDrawer = document.querySelector("cart-drawer");

    // Update cart drawer
    if (sections["cart-drawer"] && cartDrawer) {
      const wasActive = cartDrawer.classList.contains("active");
      const scrollTop =
        cartDrawer.querySelector(".drawer__inner")?.scrollTop || 0;

      // Parse the section HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = sections["cart-drawer"];

      // Find the cart-drawer element or use the section content
      const newDrawerContent = tempDiv.querySelector("cart-drawer");
      if (newDrawerContent) {
        cartDrawer.innerHTML = newDrawerContent.innerHTML;
      } else {
        // Fallback: use the section content directly (may be wrapped in shopify-section)
        const sectionContent = tempDiv.querySelector(".shopify-section");
        if (sectionContent) {
          const innerDrawer = sectionContent.querySelector("cart-drawer");
          if (innerDrawer) {
            cartDrawer.innerHTML = innerDrawer.innerHTML;
          }
        }
      }

      // Restore state
      if (wasActive) {
        cartDrawer.classList.add("active", "animate");
      }

      const drawerInner = cartDrawer.querySelector(".drawer__inner");
      if (drawerInner) drawerInner.scrollTop = scrollTop;

      // Update empty state
      const hasItems = cartDrawer.querySelector(".cart-item") !== null;
      cartDrawer.classList.toggle("is-empty", !hasItems);

      const drawerItems = cartDrawer.querySelector("cart-drawer-items");
      if (drawerItems) drawerItems.classList.toggle("is-empty", !hasItems);

      // Re-attach overlay click handler
      const overlay = cartDrawer.querySelector("#CartDrawer-Overlay");
      if (overlay) overlay.addEventListener("click", () => cartDrawer.close());
    }

    // Update cart icon bubble
    if (sections["cart-icon-bubble"]) {
      const cartBubble = document.getElementById("cart-icon-bubble");
      if (cartBubble) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = sections["cart-icon-bubble"];
        const newContent = tempDiv.querySelector(".shopify-section");
        if (newContent) {
          cartBubble.innerHTML = newContent.innerHTML;
        } else {
          // Direct content
          cartBubble.innerHTML = tempDiv.innerHTML;
        }
      }
    }
  }
}

// Free Gift Display Manager
// Free Gift Display - No longer needed (gift displayed in line items)
class FreeGiftDisplay {
  update(cart) {
    // Free gift is now displayed in the line items, not in a separate section
  }
}

// Mystery Gift Manager Class
// Handles mystery gift eligibility, selection, and cart management
class MysteryGiftManager {
  constructor() {
    this.mysteryGiftProduct = null;
    this.selectedVariant = null;
    this.isEligible = false;
    this.isAddingToCart = false;
    this.isRemovingFromCart = false;

    // Initialize on construction
    this.init();
  }

  init() {
    // Check for email campaign on load
    this.checkForEmailCampaign();

    // Fetch the mystery gift product
    this.fetchMysteryGiftProduct();
  }

  /**
   * Check URL parameters for email campaign triggers
   * Sets a cookie if found
   */
  checkForEmailCampaign() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams) {
        const searchString = urlParams.toString().toLowerCase();
        const triggers = CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_URL_TRIGGERS;

        for (const trigger of triggers) {
          if (searchString.includes(trigger.toLowerCase())) {
            // Set cookie for configured duration
            const expiryDate = new Date();
            expiryDate.setTime(
              expiryDate.getTime() +
                CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_COOKIE_DURATION_HOURS *
                  60 *
                  60 *
                  1000,
            );
            document.cookie = `${CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_COOKIE_NAME}=true; expires=${expiryDate.toUTCString()}; path=/`;
            Logger.log("[MysteryGift] Email campaign detected, cookie set");
            return true;
          }
        }
      }
    } catch (error) {
      Logger.error("[MysteryGift] Error checking email campaign:", error);
    }
    return false;
  }

  /**
   * Check if mystery gift cookie exists
   */
  hasCookie() {
    return document.cookie
      .split(";")
      .some((item) =>
        item
          .trim()
          .startsWith(`${CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_COOKIE_NAME}=`),
      );
  }

  /**
   * Check if mystery gift is already in cart
   */
  hasGiftInCart(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(
      (item) => item.properties && item.properties._mystery_gift === "true",
    );
  }

  /**
   * Get mystery gift item from cart
   */
  getGiftFromCart(cart) {
    if (!cart || !cart.items) return null;
    return cart.items.find(
      (item) => item.properties && item.properties._mystery_gift === "true",
    );
  }

  /**
   * Fetch the mystery gift product data
   */
  async fetchMysteryGiftProduct() {
    try {
      const fetchFn = window._originalFetch || window.fetch;

      // Try direct product fetch first
      const directResponse = await fetchFn(
        `/products/${CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_HANDLE}.json`,
      );
      if (directResponse.ok) {
        const data = await directResponse.json();
        this.mysteryGiftProduct = data.product;
        Logger.log(
          "[MysteryGift] Product fetched:",
          this.mysteryGiftProduct?.title,
        );
        return;
      }

      // Fallback: search through products.json pages
      let page = 1;
      const maxPages = 5;

      while (page <= maxPages) {
        const response = await fetchFn(`/products.json?limit=250&page=${page}`);
        if (!response.ok) break;

        const data = await response.json();
        const product = data.products.find(
          (p) => p.handle === CART_ENHANCEMENT_CONFIG.MYSTERY_GIFT_HANDLE,
        );

        if (product) {
          this.mysteryGiftProduct = product;
          Logger.log("[MysteryGift] Product found on page", page);
          return;
        }

        if (data.products.length < 250) break; // No more pages
        page++;
      }

      Logger.log("[MysteryGift] Product not found");
    } catch (error) {
      Logger.error("[MysteryGift] Error fetching product:", error);
    }
  }

  /**
   * Check eligibility for mystery gift
   * Returns true if user is eligible and gift is not already in cart
   */
  checkEligibility(cart) {
    // Must have cookie from email campaign
    const hasCookie = this.hasCookie();

    // Must have items in cart
    const hasItems = cart && cart.item_count > 0;

    // Must not already have mystery gift in cart
    const alreadyInCart = this.hasGiftInCart(cart);

    // Must have product data loaded
    const hasProduct = this.mysteryGiftProduct !== null;

    this.isEligible = hasCookie && hasItems && !alreadyInCart && hasProduct;

    Logger.log("[MysteryGift] Eligibility check:", {
      hasCookie,
      hasItems,
      alreadyInCart,
      hasProduct,
      isEligible: this.isEligible,
    });

    return this.isEligible;
  }

  /**
   * Get available variants for selection
   */
  getAvailableVariants() {
    if (!this.mysteryGiftProduct || !this.mysteryGiftProduct.variants) {
      return [];
    }
    return this.mysteryGiftProduct.variants.filter(
      (v) => v.available !== false,
    );
  }

  /**
   * Add mystery gift to cart with selected variant
   */
  async addToCart(variantId) {
    if (this.isAddingToCart || this.isRemovingFromCart) {
      Logger.log("[MysteryGift] Operation in progress, skipping");
      return false;
    }

    if (!variantId) {
      Logger.error("[MysteryGift] No variant ID provided");
      return false;
    }

    try {
      this.isAddingToCart = true;
      const fetchFn = window._originalFetch || window.fetch;

      // Find the variant
      const variant = this.mysteryGiftProduct?.variants?.find(
        (v) => v.id.toString() === variantId.toString(),
      );
      if (variant) {
        this.selectedVariant = variant;
      }

      const response = await fetchFn(`${routes.cart_add_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: variantId.toString(),
          quantity: 1,
          properties: {
            _mystery_gift: "true",
          },
        }),
      });

      if (response.ok) {
        Logger.log("[MysteryGift] Added to cart");

        // Hide selection UI
        this.hideSelectionUI();

        // Wait for Shopify to process
        await new Promise((resolve) => setTimeout(resolve, 600));

        // Refresh cart drawer
        const cartResponse = await fetchFn(`${routes.cart_url}.js`);
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          await refreshCartDrawerWithReinit(cart.items.length);

          // Update all UI components
          if (window.cartDrawerEnhancements) {
            window.cartDrawerEnhancements.progressBar.update(cart, true);
            window.cartDrawerEnhancements.itemCounter.reinitElements();
            window.cartDrawerEnhancements.itemCounter.update(cart);
          }
        }

        return true;
      } else {
        Logger.error("[MysteryGift] Failed to add to cart:", response.status);
        return false;
      }
    } catch (error) {
      Logger.error("[MysteryGift] Error adding to cart:", error);
      return false;
    } finally {
      this.isAddingToCart = false;
    }
  }

  /**
   * Remove mystery gift from cart (when cart becomes empty or ineligible)
   */
  async removeFromCart(cart) {
    if (this.isRemovingFromCart || this.isAddingToCart) {
      return false;
    }

    const giftItem = this.getGiftFromCart(cart);
    if (!giftItem) {
      return false;
    }

    try {
      this.isRemovingFromCart = true;
      const fetchFn = window._originalFetch || window.fetch;

      const response = await fetchFn("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0,
        }),
      });

      if (response.ok) {
        Logger.log("[MysteryGift] Removed from cart");
        this.selectedVariant = null;

        // Wait for Shopify to process
        await new Promise((resolve) => setTimeout(resolve, 600));

        // Refresh cart drawer
        const cartResponse = await fetchFn(`${routes.cart_url}.js`);
        if (cartResponse.ok) {
          const updatedCart = await cartResponse.json();
          await refreshCartDrawerWithReinit(updatedCart.items.length);

          // Update all UI components
          if (window.cartDrawerEnhancements) {
            window.cartDrawerEnhancements.progressBar.update(updatedCart, true);
            window.cartDrawerEnhancements.itemCounter.reinitElements();
            window.cartDrawerEnhancements.itemCounter.update(updatedCart);
          }
        }

        return true;
      }

      return false;
    } catch (error) {
      Logger.error("[MysteryGift] Error removing from cart:", error);
      return false;
    } finally {
      this.isRemovingFromCart = false;
    }
  }

  /**
   * Check and manage mystery gift based on cart state
   */
  async checkAndManage(cart) {
    // Skip if operations in progress
    if (this.isAddingToCart || this.isRemovingFromCart) {
      return;
    }

    // Remove if cart is empty or has only the mystery gift
    if (!cart || cart.item_count === 0) {
      return;
    }

    // Count non-mystery-gift items
    const regularItemCount = cart.items.filter((item) => {
      return !(item.properties && item.properties._mystery_gift === "true");
    }).length;

    // If only mystery gift in cart (no regular items), remove it
    if (regularItemCount === 0 && this.hasGiftInCart(cart)) {
      await this.removeFromCart(cart);
      return;
    }

    // Check eligibility and show/hide selection UI
    const isEligible = this.checkEligibility(cart);

    if (isEligible) {
      this.showSelectionUI();
    } else {
      this.hideSelectionUI();
    }
  }

  /**
   * Show the mystery gift selection UI
   */
  showSelectionUI() {
    const container = document.getElementById("mystery-gift-selection");
    if (container) {
      container.style.display = "block";
      this.renderSelectionUI();
    }
  }

  /**
   * Hide the mystery gift selection UI
   */
  hideSelectionUI() {
    const container = document.getElementById("mystery-gift-selection");
    if (container) {
      container.style.display = "none";
    }
  }

  /**
   * Render the mystery gift selection UI content
   */
  renderSelectionUI() {
    const container = document.getElementById("mystery-gift-selection");
    if (!container || !this.mysteryGiftProduct) return;

    const variants = this.getAvailableVariants();
    if (variants.length === 0) return;

    // Get product image
    const productImage =
      this.mysteryGiftProduct.images?.[0]?.src ||
      this.mysteryGiftProduct.image?.src ||
      "";

    container.innerHTML = `
      <div class="mystery-gift-selection__inner">
        <div class="mystery-gift-selection__image">
          ${productImage ? `<img src="${productImage}" alt="${this.mysteryGiftProduct.title}" />` : ""}
        </div>
        <div class="mystery-gift-selection__content">
          <h4 class="mystery-gift-selection__title">CLAIM YOUR FREE MYSTERY GIFT!</h4>
          <p class="mystery-gift-selection__subtitle">Select your size:</p>
          <select id="mystery-gift-variant-select" class="mystery-gift-selection__select">
            <option value="">Choose size...</option>
            ${variants.map((v) => `<option value="${v.id}">${v.title}</option>`).join("")}
          </select>
          <button id="mystery-gift-add-btn" class="mystery-gift-selection__button" disabled>
            ADD TO CART
          </button>
        </div>
      </div>
    `;

    // Add event listeners
    const select = container.querySelector("#mystery-gift-variant-select");
    const button = container.querySelector("#mystery-gift-add-btn");

    if (select && button) {
      select.addEventListener("change", (e) => {
        button.disabled = !e.target.value;
      });

      button.addEventListener("click", async () => {
        const variantId = select.value;
        if (variantId) {
          button.disabled = true;
          button.textContent = "ADDING...";
          await this.addToCart(variantId);
        }
      });
    }
  }
}

// Item Counter Manager
class ItemCounter {
  constructor() {
    // State tracking
    this.state = {
      count: 0,
      isVisible: false,
    };

    this.reinitElements();
  }

  reinitElements() {
    this.counterElement = document.getElementById("cart-drawer-item-counter");
    this.countElement = document.getElementById("cart-item-count");
  }

  update(cart) {
    if (!cart) return;

    // Just update the count text - don't manipulate display
    const countElement = document.getElementById("cart-item-count");
    if (countElement) {
      countElement.textContent = cart.item_count;
    }

    // Update state for reference
    this.state.count = cart.item_count;
    this.state.isVisible = cart.item_count > 0;
  }
}

// Helper to refresh cart drawer with proper custom element reinitialization
async function refreshCartDrawerWithReinit(
  expectedItemCount = null,
  retryCount = 0,
) {
  const fetchFn = window._originalFetch || window.fetch;
  const maxRetries = 1;
  const cartDrawer = document.querySelector("cart-drawer");

  if (!cartDrawer) return;

  try {
    const response = await fetchFn(
      `${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`,
    );
    if (!response.ok) {
      Logger.error("[Refresh] Failed to fetch sections");
      return;
    }

    const data = await response.json();
    const cartDrawerHtml = data["cart-drawer"];
    const cartBubbleHtml = data["cart-icon-bubble"];

    if (cartDrawerHtml) {
      const wasActive = cartDrawer.classList.contains("active");
      const scrollTop =
        cartDrawer.querySelector(".drawer__inner")?.scrollTop || 0;

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = cartDrawerHtml;
      const newCartItems = tempDiv.querySelectorAll(".cart-item");

      if (
        expectedItemCount !== null &&
        newCartItems.length !== expectedItemCount &&
        retryCount < maxRetries
      ) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        return refreshCartDrawerWithReinit(expectedItemCount, retryCount + 1);
      }

      const newSection =
        tempDiv.querySelector("cart-drawer") || tempDiv.firstElementChild;

      if (newSection) {
        cartDrawer.innerHTML = newSection.innerHTML;

        if (wasActive) {
          cartDrawer.classList.add("active", "animate");
        }

        const drawerInner = cartDrawer.querySelector(".drawer__inner");
        if (drawerInner) {
          drawerInner.scrollTop = scrollTop;
        }

        const hasItems = cartDrawer.querySelector(".cart-item") !== null;
        cartDrawer.classList.toggle("is-empty", !hasItems);

        const drawerItems = cartDrawer.querySelector("cart-drawer-items");
        if (drawerItems) {
          drawerItems.classList.toggle("is-empty", !hasItems);
        }

        const overlay = cartDrawer.querySelector("#CartDrawer-Overlay");
        if (overlay) {
          overlay.addEventListener("click", () => cartDrawer.close());
        }
      }
    }

    if (cartBubbleHtml) {
      const cartBubble = document.getElementById("cart-icon-bubble");
      if (cartBubble) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = cartBubbleHtml;
        const newContent =
          tempDiv.querySelector(".shopify-section") ||
          tempDiv.firstElementChild;
        if (newContent) {
          cartBubble.innerHTML = newContent.innerHTML;
        }
      }
    }
  } catch (error) {
    Logger.error("[Refresh] Error:", error);
  }
}

// Helper to refresh cart drawer content by triggering Shopify's native section rendering
async function refreshCartDrawerContent() {
  try {
    const fetchFn = window._originalFetch || window.fetch;

    // Fetch fresh sections from Shopify
    const response = await fetchFn(
      `${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`,
    );
    if (!response.ok) {
      Logger.error("Failed to fetch cart sections");
      return false;
    }

    const data = await response.json();

    // Try to use CartDrawerItems native rendering if available
    const cartDrawerItems = document.querySelector("cart-drawer-items");
    if (
      cartDrawerItems &&
      typeof cartDrawerItems.getSectionsToRender === "function"
    ) {
      // Use native section rendering
      const sections = cartDrawerItems.getSectionsToRender();
      sections.forEach((section) => {
        const sectionHtml = data.sections[section.section];
        if (sectionHtml) {
          const elementToReplace =
            document
              .getElementById(section.id)
              ?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (elementToReplace) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHtml, "text/html");
            const newElement =
              doc.querySelector(section.selector) ||
              doc.querySelector(".shopify-section");
            if (newElement) {
              elementToReplace.innerHTML = newElement.innerHTML;
            }
          }
        }
      });
      Logger.log("Cart drawer content refreshed via native method");
    } else {
      // Fallback: Replace cart drawer content directly
      if (data.sections && data.sections["cart-drawer"]) {
        const cartDrawer = document.querySelector("cart-drawer");
        const cartDrawerSection = document.getElementById("CartDrawer");

        if (cartDrawerSection) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(
            data.sections["cart-drawer"],
            "text/html",
          );
          const newContent =
            doc.querySelector("#CartDrawer") ||
            doc.querySelector(".drawer__inner");

          if (newContent) {
            // Store scroll position and drawer state
            const drawerInner = cartDrawer?.querySelector(".drawer__inner");
            const scrollTop = drawerInner?.scrollTop || 0;
            const isActive = cartDrawer?.classList.contains("active");

            // Update content
            cartDrawerSection.innerHTML = newContent.innerHTML;

            // Restore state
            if (drawerInner) drawerInner.scrollTop = scrollTop;
            if (isActive && cartDrawer) {
              cartDrawer.classList.add("active", "animate");
            }

            Logger.log("Cart drawer content refreshed via fallback");
          }
        }
      }
    }

    // Update cart icon bubble
    if (data.sections && data.sections["cart-icon-bubble"]) {
      const cartBubbleSection = document.getElementById("cart-icon-bubble");
      if (cartBubbleSection) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(
          data.sections["cart-icon-bubble"],
          "text/html",
        );
        const newBubble = doc.querySelector(".shopify-section");
        if (newBubble) {
          cartBubbleSection.innerHTML = newBubble.innerHTML;
        }
      }
    }

    // Update empty states
    const cartDrawer = document.querySelector("cart-drawer");
    const drawerItems = cartDrawer?.querySelector("cart-drawer-items");
    const isEmpty = !drawerItems?.querySelector(".cart-item");

    if (cartDrawer) cartDrawer.classList.toggle("is-empty", isEmpty);
    if (drawerItems) drawerItems.classList.toggle("is-empty", isEmpty);

    return true;
  } catch (error) {
    Logger.error("Error refreshing cart drawer:", error);
    return false;
  }
}

// Main Cart Drawer Enhancements Manager
class CartDrawerEnhancements {
  constructor() {
    this.timer = new CartTimer();
    this.progressBar = new CartProgressBar();
    this.freeGiftManager = new FreeGiftManager();
    this.freeGiftDisplay = new FreeGiftDisplay();
    this.mysteryGiftManager = new MysteryGiftManager();
    this.itemCounter = new ItemCounter();
    this.cartWatcher = new CartWatcher();
    this.cartDrawer = null;
    this.lastCart = null;
    this.lastCartWatcherEventTime = 0; // Track when CartWatcher last emitted an event
    this.pendingUpdate = null; // Debounce pending updates
    this.UPDATE_DEBOUNCE = 200; // ms to debounce updates (batch rapid events from bundle apps)
    this.init();
  }

  init() {
    // Expose globally so cart.js can update state before section replacement
    window.cartEnhancements = this;

    // Store original fetch early (before any other scripts might intercept it)
    // This is used by CartWatcher and FreeGiftManager to avoid circular dependencies
    if (!window._originalFetch) {
      window._originalFetch = window.fetch.bind(window);
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Update state from cart data - called by cart.js BEFORE sections are replaced
   * This ensures MutationObserver applies the NEW state when DOM changes
   */
  updateState(cart) {
    if (!cart) return;

    Logger.log("[CartEnhancements] updateState:", {
      itemCount: cart.item_count,
      total: (cart.total_price / 100).toFixed(2),
    });

    // Store this cart data
    this.lastCart = cart;

    // Update UI components immediately (no cart modifications)
    this.progressBar.update(cart);
    this.itemCounter.update(cart);

    // Eagerly check gift eligibility from cart.js response.
    // This fires ~1-2s before CartWatcher detects the change,
    // so the gift add starts sooner on slow connections.
    if (cart.item_count > 0 && !this.freeGiftManager.isProcessing) {
      const timeSinceOp =
        Date.now() - (this.freeGiftManager.lastOperationTime || 0);
      if (timeSinceOp > 1500) {
        this.freeGiftManager.checkAndManage(cart);
      }
    }
  }

  setup() {
    this.cartDrawer = document.querySelector("cart-drawer");
    if (!this.cartDrawer) {
      setTimeout(() => this.setup(), 100);
      return;
    }

    // On back/forward navigation, the browser may serve a cached page
    // (either bfcache or HTTP cache) with a stale cart drawer and count.
    // Fetch both cart-drawer and cart-icon-bubble sections to sync the UI.
    window.addEventListener("pageshow", async (event) => {
      try {
        const fetchFn = window._originalFetch || window.fetch;
        const resp = await fetchFn(
          `${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`,
        );
        if (!resp.ok) return;
        const data = await resp.json();

        // Update cart icon bubble
        const cartBubble = document.getElementById("cart-icon-bubble");
        if (cartBubble && data["cart-icon-bubble"]) {
          const tmp = document.createElement("div");
          tmp.innerHTML = data["cart-icon-bubble"];
          const content =
            tmp.querySelector(".shopify-section") || tmp.firstElementChild;
          if (content) cartBubble.innerHTML = content.innerHTML;
        }

        // Update cart drawer to prevent stale/empty drawer after back navigation
        if (data["cart-drawer"] && this.cartDrawer) {
          const tmpDrawer = document.createElement("div");
          tmpDrawer.innerHTML = data["cart-drawer"];
          const newSection =
            tmpDrawer.querySelector("cart-drawer") || tmpDrawer.firstElementChild;
          if (newSection) {
            const wasActive = this.cartDrawer.classList.contains("active");
            this.cartDrawer.innerHTML = newSection.innerHTML;
            if (wasActive) {
              this.cartDrawer.classList.add("active", "animate");
            }
            const hasItems = this.cartDrawer.querySelector(".cart-item") !== null;
            this.cartDrawer.classList.toggle("is-empty", !hasItems);
            const overlay = this.cartDrawer.querySelector("#CartDrawer-Overlay");
            if (overlay) {
              overlay.addEventListener("click", () => this.cartDrawer.close());
            }
          }
        }
      } catch (e) {
        // Silently fail — the drawer will sync on next cart interaction
      }
    });

    // Initialize CartWatcher - this will detect all cart changes using PerformanceObserver
    this.cartWatcher.init();

    // Listen for cart_changed events from CartWatcher
    // This is much cleaner than intercepting fetch calls!
    this.lastCartWatcherEventTime = 0;
    window.addEventListener("cart_changed", (e) => {
      if (e.detail && e.detail.newCart) {
        const timestamp = Date.now();
        const newCart = e.detail.newCart;
        const changes = e.detail.changes || { added: [], removed: [] };

        // Check if we just completed a free gift operation (ignore our own changes)
        const timeSinceFreeGiftOp =
          timestamp - (this.freeGiftManager.lastOperationTime || 0);
        // Check by _free_gift property only (not handle) so gift product can be changed
        const freeGiftAdded = changes.added?.some(
          (item) => item.properties?._free_gift === "true",
        );
        const freeGiftRemoved = changes.removed?.some(
          (item) => item.properties?._free_gift === "true",
        );

        // If we just added/removed the gift ourselves, update data components but skip gift re-check
        if ((freeGiftAdded || freeGiftRemoved) && timeSinceFreeGiftOp < 2000) {
          Logger.log(
            `[CartEnhancements] Gift change from our operation - updating UI only`,
          );
          this.progressBar.update(newCart);
          this.itemCounter.update(newCart);
          // Refresh sections to ensure cart drawer reflects the change
          const currentItemCount =
            this.cartDrawer?.querySelectorAll(".cart-item").length || 0;
          if (Math.abs(currentItemCount - newCart.item_count) > 0) {
            refreshCartDrawerWithReinit(newCart.item_count);
          }
          return;
        }

        this.lastCartWatcherEventTime = timestamp;

        // Get triggering request info from event detail
        const triggeringRequest = e.detail.triggeringRequest;

        Logger.log("[CartEnhancements] cart_changed event received:", {
          added: changes.added?.length || 0,
          removed: changes.removed?.length || 0,
          newItemCount: newCart.item_count,
        });

        // Debounce updates - cancel pending and schedule new one
        if (this.pendingUpdate) {
          clearTimeout(this.pendingUpdate);
        }

        this.pendingUpdate = setTimeout(async () => {
          this.pendingUpdate = null;

          // Update UI components immediately (no fetch needed)
          this.progressBar.update(newCart);
          this.itemCounter.update(newCart);

          // Handle gift logic FIRST (the check itself is fast, no network needed)
          await this.onCartUpdate(newCart);

          // Then refresh cart drawer HTML if item count changed
          // Skip if a gift operation just happened (it handles its own section refresh)
          const recentGiftOp =
            Date.now() - (this.freeGiftManager.lastOperationTime || 0) < 2000;
          if (!recentGiftOp) {
            const currentItemCount =
              this.cartDrawer?.querySelectorAll(".cart-item").length || 0;
            if (Math.abs(currentItemCount - newCart.item_count) > 0) {
              refreshCartDrawerWithReinit(newCart.item_count);
            }
          }
        }, this.UPDATE_DEBOUNCE);
      }
    });

    // Listen for legacy cart:updated events (from other parts of the theme)
    window.addEventListener("cart:updated", (e) => {
      if (e.detail && e.detail.cart) {
        setTimeout(() => this.onCartUpdate(e.detail.cart), 100);
      }
    });

    // Try to override cart drawer open method if it exists
    if (this.cartDrawer.open && typeof this.cartDrawer.open === "function") {
      const originalOpen = this.cartDrawer.open.bind(this.cartDrawer);
      this.cartDrawer.open = (triggeredBy) => {
        originalOpen(triggeredBy);
        setTimeout(() => this.onCartOpen(), 300);
      };
    }

    // Watch for drawer becoming active (main method to detect open)
    const classObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          if (this.cartDrawer.classList.contains("active")) {
            setTimeout(() => this.onCartOpen(), 300);
          }
        }
      });
    });

    classObserver.observe(this.cartDrawer, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Initial check - if drawer is already active
    if (this.cartDrawer.classList.contains("active")) {
      setTimeout(() => this.onCartOpen(), 300);
    }

    // Fetch cart data once on initial load
    this.fetchAndUpdateCart();
  }

  async fetchAndUpdateCart() {
    try {
      const fetchFn = window._originalFetch || window.fetch;
      const response = await fetchFn(`${routes.cart_url}.js`);
      if (response.ok) {
        const cart = await response.json();
        this.lastCart = cart;
        await this.updateAll(cart);
      }
    } catch (error) {
      Logger.error("Error fetching cart:", error);
    }
  }

  async onCartOpen() {
    // Debounce rapid open events (can happen during DOM refresh)
    if (this._lastOpenTime && Date.now() - this._lastOpenTime < 500) {
      return;
    }
    this._lastOpenTime = Date.now();

    try {
      const fetchFn = window._originalFetch || window.fetch;
      const response = await fetchFn(`${routes.cart_url}.js`);
      if (response.ok) {
        const cart = await response.json();
        this.lastCart = cart;

        // Only initialize timer if cart has items
        if (cart && cart.item_count > 0) {
          this.timer.init();
        }

        this.updateAll(cart);
      }
    } catch (error) {
      Logger.error("Error fetching cart:", error);
      // Still try to update with default currency
      this.progressBar.update({
        currency: "AUD",
        item_count: 0,
        items: [],
        total_price: 0,
      });
    }
  }

  async onCartUpdate(cart) {
    const timestamp = Date.now();

    // Skip if we just processed a free gift change (avoid loops)
    const timeSinceFreeGiftOp =
      timestamp - (this.freeGiftManager.lastOperationTime || 0);
    if (timeSinceFreeGiftOp < 1500) {
      Logger.log(
        `[CartEnhancements] Skipping gift check - operation ${timeSinceFreeGiftOp}ms ago`,
      );
      // Still update UI
      this.progressBar.update(cart);
      this.itemCounter.update(cart);
      return;
    }

    Logger.log(`[CartEnhancements] onCartUpdate:`, {
      itemCount: cart?.item_count,
      total: cart?.total_price ? (cart.total_price / 100).toFixed(2) : "0",
    });

    this.lastCart = cart;
    await this.updateAll(cart);
  }

  async updateAll(cart) {
    // Handle timer based on cart state
    if (!cart || cart.item_count === 0) {
      this.timer.stop();
    } else {
      if (!this.timer.isRunning) {
        this.timer.init();
      }
    }

    // Update progress bar (state-based, efficient)
    this.progressBar.update(cart);

    // Update item counter (lightweight)
    this.itemCounter.update(cart);

    // Check and manage free gift (has internal debouncing)
    if (cart && cart.item_count > 0 && !this.freeGiftManager.isProcessing) {
      await this.freeGiftManager.checkAndManage(cart);
    }

    // Check and manage mystery gift
    if (
      !this.mysteryGiftManager.isAddingToCart &&
      !this.mysteryGiftManager.isRemovingFromCart
    ) {
      await this.mysteryGiftManager.checkAndManage(cart);
    }
  }
}

// Initialize the enhancements
window.cartDrawerEnhancements = new CartDrawerEnhancements();
